import { ReactNode } from 'react';
import { alpha, Box, Card, CardContent, Stack, Typography, useTheme } from '@mui/material';
import { TrendingDown, TrendingUp } from '@mui/icons-material';

export interface StatCardProps {
  icon?: ReactNode;
  label: string;
  value: ReactNode;
  helper?: ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';
  delta?: number; // percentage change
  deltaLabel?: string;
}

export default function StatCard({
  icon,
  label,
  value,
  helper,
  color = 'primary',
  delta,
  deltaLabel,
}: StatCardProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const paletteColor =
    color === 'primary'
      ? theme.palette.primary
      : color === 'success'
      ? theme.palette.success
      : color === 'warning'
      ? theme.palette.warning
      : color === 'error'
      ? theme.palette.error
      : color === 'info'
      ? theme.palette.info
      : theme.palette.secondary;
  const accent = paletteColor.main;

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        minHeight: 128,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: isDark ? alpha(accent, 0.08) : theme.palette.background.paper,
        border: `1px solid ${isDark ? alpha(accent, 0.25) : theme.palette.divider}`,
        transition: 'transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: alpha(accent, isDark ? 0.5 : 0.35),
          boxShadow: isDark
            ? `0 8px 24px ${alpha(accent, 0.2)}`
            : `0 8px 24px ${alpha(theme.palette.common.black, 0.08)}`,
        },
        // decorative blob in the corner
        '&::after': {
          content: '""',
          position: 'absolute',
          top: -40,
          right: -40,
          width: 140,
          height: 140,
          borderRadius: '50%',
          backgroundColor: alpha(accent, isDark ? 0.12 : 0.08),
          pointerEvents: 'none',
        },
      }}
    >
      <CardContent
        sx={{
          p: 2.75,
          position: 'relative',
          zIndex: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          '&:last-child': { pb: 2.75 },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography
            variant="caption"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              fontSize: 11,
              fontWeight: 700,
              color: 'text.secondary',
            }}
          >
            {label}
          </Typography>
          {icon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: alpha(accent, isDark ? 0.2 : 0.12),
                color: accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                '& svg': { fontSize: 22 },
              }}
            >
              {icon}
            </Box>
          )}
        </Stack>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography
            sx={{
              fontSize: 32,
              fontWeight: 700,
              lineHeight: 1.15,
              color: 'text.primary',
              wordBreak: 'break-word',
            }}
            component="div"
          >
            {value}
          </Typography>
        </Box>

        {(helper || delta != null) && (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            {delta != null && (
              <>
                {delta >= 0 ? (
                  <TrendingUp sx={{ color: 'success.main', fontSize: 18 }} />
                ) : (
                  <TrendingDown sx={{ color: 'error.main', fontSize: 18 }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: delta >= 0 ? 'success.main' : 'error.main',
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  {delta >= 0 ? '+' : ''}
                  {delta}%
                </Typography>
                {deltaLabel && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                    {deltaLabel}
                  </Typography>
                )}
              </>
            )}
            {helper && delta == null && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                {helper}
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
